import type { SortOrder } from '@movingimage-evp/mi-ui-component-library';
import type { KeyboardEvent, MouseEvent } from 'react';

import type { Webcast, WebcastOrder } from '../../../../../generated/graphql-manager';
import type { Props } from '../grid/grid';

import {
  PresentationIcon,
  Table,
  Tooltip,
  classNames,
  getNextSorting,
  usePrevious,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { StateBadge } from '../../../../../components/state-badge';
import { State, UserStatus } from '../../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../../routes';
import { addParamToPath, convertStringOrderObject, formatEventStartDate } from '../../../../../utils';
import { Languages } from '../../../../components/language';
import { useCurrentUserRolesForWebcast } from '../../../../hooks/use-current-user-roles-for-webcast';
import { useUserPermissions } from '../../../../hooks/user-permissions';
import { useUiState } from '../../../../providers/ui-state-provider/ui-state-provider';
import { ONDEMAND_STATE } from '../../webcasts';
import { ActionButtons } from '../action-buttons/action-buttons';

import styles from './list.module.css';

type WebcastOrderId = 'PLANNED_AT' | 'STATE' | 'CREATOR_ID';

type ListViewRowProps = {
  webcast: Webcast;
  menuPosition?: 'top-right' | 'right';
  onArchiveWebcast: (id: Webcast['id']) => void;
  onDeleteWebcast: (id: Webcast['id']) => void;
  onDuplicateWebcast: (id: Webcast['id'], name: Webcast['name']) => void;
  onUnarchiveWebcast: (id: Webcast['id']) => void;
};

function ListViewRow({
  webcast,
  menuPosition,
  onArchiveWebcast,
  onDeleteWebcast,
  onDuplicateWebcast,
  onUnarchiveWebcast,
}: ListViewRowProps) {
  const { t } = useTranslation();
  const { isAccessToEventSetupAllowed } = useUserPermissions();
  const { isOnlyModeratorForEvent } = useCurrentUserRolesForWebcast(webcast.id);
  const navigate = useNavigate();
  const routes = useAbsoluteRoutes();

  const handleNavigation = (webcastId: Webcast['id'], state: Webcast['state']) => {
    if (isOnlyModeratorForEvent || !isAccessToEventSetupAllowed) {
      navigate(addParamToPath(routes.operator_messaging, webcastId));
      return;
    }

    if (state === State.POSTLIVE || state === ONDEMAND_STATE || state === State.ARCHIVED) {
      navigate(addParamToPath(routes.eventSummary, webcastId));
      return;
    }

    navigate(addParamToPath(routes.webcastSetup_setupSummary, webcastId));
  };

  const handleRowClick = (event: MouseEvent, webcastId: Webcast['id'], state: Webcast['state']) => {
    if (event.target instanceof HTMLButtonElement) return;

    handleNavigation(webcastId, state);
  };

  const handleKeyDown = (event: KeyboardEvent, webcastId: Webcast['id'], state: Webcast['state']) => {
    if (event.key !== 'Enter') return;

    handleNavigation(webcastId, state);
  };

  const { id, name, plannedStartAt, state, primaryLang, additionalLangs, presentations, coverImageUrl } = webcast;
  const isInitializing = state === State.INITIALIZING;
  const languages = [primaryLang, ...additionalLangs];

  const creator = webcast.creator;
  const creatorName = [creator?.firstName, creator?.lastName].filter(Boolean).join(' ').trim();

  let creatorEmail = '';
  let creatorDisplayName = '';

  if (creator) {
    creatorEmail = creator.email;
    creatorDisplayName = creatorName || creatorEmail;

    if (creator.status !== UserStatus.ACTIVE) {
      creatorDisplayName = t(`common.userStatus.${creator.status}`);
    }
  }

  return (
    <Table.Row
      role="button"
      data-testid="webcast-row-information"
      highlightOnHover
      focusable={true}
      className={classNames(isInitializing && styles.rowDisabled)}
      onClick={(event) => handleRowClick(event, id, state)}
      onKeyDown={(event) => handleKeyDown(event, id, state)}
    >
      <Table.Cell>
        <div className={styles.title} data-testid="webcast-name">
          <div
            className={styles.thumbnail}
            data-testid="webcast-row-thumbnail"
            style={coverImageUrl ? { backgroundImage: `url(${coverImageUrl})` } : undefined}
          />

          <span className={styles.text}>{name}</span>

          <div data-testid="webcast-presentations" className={styles.presentations}>
            {presentations.map((presentation) => (
              <PresentationIcon key={presentation.language} />
            ))}
          </div>
        </div>
      </Table.Cell>

      <Table.Cell data-testid="webcast-planned-start">{formatEventStartDate(plannedStartAt)}</Table.Cell>

      <Table.Cell data-testid="webcast-event-creator">
        <Tooltip label={creatorEmail} hidden={!creatorName}>
          <span>{creatorDisplayName}</span>
        </Tooltip>
      </Table.Cell>

      <Table.Cell data-testid="webcast-state">
        <StateBadge state={state} />
      </Table.Cell>

      <Table.Cell data-testid="webcast-languages">
        <Languages languages={languages} />
      </Table.Cell>

      <Table.ActionCell>
        <ActionButtons
          webcast={webcast}
          menuPosition={menuPosition}
          onArchiveWebcast={onArchiveWebcast}
          onDeleteWebcast={onDeleteWebcast}
          onDuplicateWebcast={onDuplicateWebcast}
          onUnarchiveWebcast={onUnarchiveWebcast}
        />
      </Table.ActionCell>
    </Table.Row>
  );
}

export function ListView({
  webcasts,
  orderBy,
  isRefetching,
  onArchiveWebcast,
  onDeleteWebcast,
  onDuplicateWebcast,
  onUnarchiveWebcast,
  onSort,
}: Props) {
  const { t } = useTranslation();
  const { getEventsScrollPosition, saveUiState } = useUiState();
  const listRef = useRef<HTMLDivElement>(null);
  const wasRefetching = usePrevious(isRefetching);

  const [sorting, setSorting] = useState<{ id: WebcastOrderId; order?: SortOrder } | undefined>(
    convertStringOrderObject(orderBy)
  );

  useEffect(() => {
    if (!sorting) return;

    const searchKey = sorting.order ? (sorting.id + '_' + sorting.order).toLocaleUpperCase() : undefined;

    onSort?.(searchKey as WebcastOrder);
  }, [onSort, sorting]);

  const handleSortChange = (columnId: WebcastOrderId) => {
    if (columnId !== sorting?.id) {
      setSorting({ id: columnId, order: 'asc' });
      return;
    }

    setSorting({ ...sorting, order: getNextSorting(sorting.order) });
  };

  const handleScroll = () => {
    saveUiState({ key: 'eventsScrollPosition', value: Math.round(listRef.current?.scrollTop || 0) });
  };

  useEffect(() => {
    listRef.current?.scrollTo({ top: getEventsScrollPosition(), behavior: 'smooth' });
  }, [getEventsScrollPosition]);

  useEffect(() => {
    if (wasRefetching && !isRefetching) {
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [isRefetching, wasRefetching]);

  return (
    <div className={styles.scroll} ref={listRef} onScroll={handleScroll}>
      <Table data-testid="webcasts-table" className={styles.table}>
        <Table.Header sticky>
          <Table.Row>
            <Table.HeaderCell>{t('manager.webcasts.list.name')}</Table.HeaderCell>

            <Table.HeaderCell
              sorting={{
                onSortingChange: () => handleSortChange('PLANNED_AT'),
                order: sorting?.id === 'PLANNED_AT' ? sorting?.order : undefined,
              }}
            >
              {t('manager.webcasts.list.date')}
            </Table.HeaderCell>

            <Table.HeaderCell
              sorting={{
                onSortingChange: () => handleSortChange('CREATOR_ID'),
                order: sorting?.id === 'CREATOR_ID' ? sorting?.order : undefined,
              }}
            >
              {t('manager.webcasts.list.eventCreator')}
            </Table.HeaderCell>

            <Table.HeaderCell
              sorting={{
                onSortingChange: () => handleSortChange('STATE'),
                order: sorting?.id === 'STATE' ? sorting?.order : undefined,
              }}
            >
              {t('manager.webcasts.list.status')}
            </Table.HeaderCell>

            <Table.HeaderCell>{t('manager.webcasts.list.languages')}</Table.HeaderCell>
            <Table.HeaderCell>{t('manager.webcasts.list.actions')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {webcasts?.map((webcast, index) => (
            <ListViewRow
              key={webcast.id}
              webcast={webcast}
              menuPosition={index === webcasts.length - 1 ? 'top-right' : 'right'}
              onArchiveWebcast={onArchiveWebcast}
              onDeleteWebcast={onDeleteWebcast}
              onDuplicateWebcast={onDuplicateWebcast}
              onUnarchiveWebcast={onUnarchiveWebcast}
            />
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}
